import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LayoutDashboard, Users, Package, ShoppingCart, Target, FileText, LogOut, Menu, X, Calculator, KeyRound, Briefcase, Database, ChevronDown, BarChart3, Globe, Network, TrendingUp, Wallet, ShieldCheck, ArrowRightLeft, FileSpreadsheet, Sparkles, Settings, UserCog, Megaphone, PanelRightClose, PanelRightOpen, RefreshCw, Tag } from 'lucide-react';
import BrandLogo from './BrandLogo';
import AnnouncementBanner from './AnnouncementBanner';
import { toast } from 'sonner';

export default function Layout({ children }) {
  const { user, branch, logout, actingBranch, setActingBranch, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only collapse toggle (persisted per browser so it survives reloads on big screens/TVs)
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try { return localStorage.getItem('qimam:sidebar-collapsed') === '1'; } catch { return false; }
  });
  const toggleDesktopSidebar = () => {
    setDesktopCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('qimam:sidebar-collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const [openGroups, setOpenGroups] = useState({ 'إدارة': false, 'التحليل': false });

  const handleLogout = () => {
    logout();
    toast.success('تم تسجيل الخروج بنجاح');
    navigate('/');
  };

  const exitActingBranch = async () => {
    setActingBranch(null);
    await refreshUser();
    navigate('/super/branches');
    toast.success('عُدت لإدارة المنطقة الشرقية');
  };

  const isSuper = user?.role === 'super_admin';
  const isActingAsBranch = isSuper && !!actingBranch;
  const isAdmin = user?.role === 'admin';
  const isManagerOrSupervisor = isAdmin || user?.role === 'supervisor' || isActingAsBranch;
  const navigation = (isSuper && !isActingAsBranch)
    ? [
        { name: 'المبيعات', href: '/super/totals', icon: Wallet },
        { name: 'تحليل شامل', href: '/super/analytics', icon: BarChart3 },
        {
          name: 'تقرير القطاع',
          icon: FileSpreadsheet,
          children: [
            { name: '1- تقرير Sharing', href: '/super/tablo/sharing', icon: FileSpreadsheet },
            { name: '2- تقرير Tableau', href: '/super/tablo/tablo', icon: FileSpreadsheet },
          ],
        },
        {
          name: 'مقارنات',
          icon: ArrowRightLeft,
          children: [
            { name: 'مقارنة المنتجات', href: '/super/product-compare', icon: Package },
            { name: 'مقارنة مبيعات', href: '/super/week-compare', icon: ArrowRightLeft },
          ],
        },
        { name: 'رسائل التحفيز', href: '/super/messages', icon: Sparkles },
        {
          name: 'التهيئة',
          icon: Settings,
          children: [
            { name: 'المنتجات', href: '/super/products', icon: Package },
            { name: 'الدول', href: '/super/countries', icon: Globe },
            { name: 'قواعد الحقول الإلزامية', href: '/super/field-rules', icon: ShieldCheck },
            { name: 'الشريط الإعلاني', href: '/super/announcement', icon: Megaphone },
            { name: 'كوبونات الخصم', href: '/super/coupons', icon: Tag },
          ],
        },
        {
          name: 'إدارة',
          icon: UserCog,
          children: [
            { name: 'إدارة الفروع', href: '/super/branches', icon: Network },
            { name: 'المستخدمين', href: '/users', icon: Users },
            { name: 'التحديث التلقائي', href: '/auto-refresh', icon: RefreshCw },
            { name: 'تغيير كلمة المرور', href: '/change-password', icon: KeyRound },
          ],
        },
      ]
    : isManagerOrSupervisor
    ? [
        { name: 'لمحة سريعة', href: '/dashboard', icon: LayoutDashboard },
        { name: 'المستهدفات', href: '/targets', icon: Target },
        {
          name: 'التحليل',
          icon: BarChart3,
          children: [
            // Branch-scoped comprehensive analytics — admin + supervisor (mirrors super-admin's تحليل شامل for the user's own branch)
            { name: 'التحليل الشامل', href: '/branch-analytics', icon: BarChart3 },
            { name: 'تحليل مبيعات الموظف', href: '/product-sales', icon: BarChart3 },
            { name: 'المبيعات حسب الدولة', href: '/sales-by-country', icon: Globe },
            { name: 'مقارنة مبيعات', href: '/branch-week-compare', icon: ArrowRightLeft },
          ],
        },
        { name: 'قسم المعالجة', href: '/processing', icon: Database },
        { name: 'المنتجات', href: '/products', icon: Package },
        { name: 'التقارير', href: '/reports', icon: FileText },
        { name: 'تسجيل مبيعات الموظف', href: '/sales', icon: ShoppingCart },
        { name: 'حاسبة الوزن الحجمي', href: '/volumetric', icon: Calculator },
        // إدارة (Users + Employees) shown to admin or super-admin acting as branch
        ...(isAdmin || isActingAsBranch
          ? [{
              name: 'إدارة',
              icon: Briefcase,
              children: [
                { name: 'المستخدمين', href: '/users', icon: Users },
                { name: 'الموظفين', href: '/employees', icon: Users },
                { name: 'التحديث التلقائي', href: '/auto-refresh', icon: RefreshCw },
              ],
            }]
          : []),
        { name: 'تغيير كلمة المرور', href: '/change-password', icon: KeyRound },
      ]
    : [
        { name: 'لمحة سريعة', href: '/dashboard', icon: LayoutDashboard },
        { name: 'تسجيل مبيعات جديدة', href: '/sales', icon: ShoppingCart },
        { name: 'مبيعاتي السابقة', href: '/product-sales', icon: Package },
        { name: 'المستهدفات', href: '/targets', icon: Target },
        { name: 'قسم المعالجة', href: '/processing', icon: Database },
        { name: 'حاسبة الوزن الحجمي', href: '/volumetric', icon: Calculator },
        { name: 'تغيير كلمة المرور', href: '/change-password', icon: KeyRound },
      ];

  // Auto-open a group if a child route is active
  React.useEffect(() => {
    navigation.forEach((item) => {
      if (item.children && item.children.some((c) => c.href === location.pathname)) {
        setOpenGroups((g) => ({ ...g, [item.name]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (name) => setOpenGroups((g) => ({ ...g, [name]: !g[name] }));

  // ─── Auto-refresh (managers only) ────────────────────────────────────
  // Reloads the whole page every 10 minutes when the manager has activated
  // it from /auto-refresh. Ideal for TV dashboards where a manager wants
  // continuously fresh data without touching the remote. The setting lives
  // in localStorage so it survives page reloads (including the reloads it
  // itself triggers). Employees can't reach the toggle UI.
  const canAutoRefresh = isAdmin || isSuper || isActingAsBranch;
  React.useEffect(() => {
    if (!canAutoRefresh) return;
    const readEnabled = () => {
      try { return localStorage.getItem('qimam:auto-refresh-enabled') === '1'; } catch { return false; }
    };
    let intervalId = null;
    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        // Skip when the tab is in the background — no point reloading if nobody's looking
        if (document.visibilityState === 'visible') window.location.reload();
      }, 600000);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    if (readEnabled()) start();
    const handler = () => (readEnabled() ? start() : stop());
    window.addEventListener('auto-refresh:changed', handler);
    // Listen for storage events too (in case the setting is toggled in another tab)
    const storageHandler = (e) => { if (e.key === 'qimam:auto-refresh-enabled') handler(); };
    window.addEventListener('storage', storageHandler);
    return () => {
      stop();
      window.removeEventListener('auto-refresh:changed', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, [canAutoRefresh]);

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      {/* Global announcement banner — shown ONLY on the "لمحة سريعة" (Dashboard)
          page per user request. Kept as the first element in the layout so iOS
          Safari renders it above the sticky mobile header on the first paint. */}
      {location.pathname === '/dashboard' && <AnnouncementBanner />}

      <div className="lg:flex">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — slides from RIGHT (mobile) / collapses to 0px width (desktop) */}
        <aside
          className={`fixed top-0 right-0 w-72 bg-card border-l border-border z-50 transform transition-all duration-300 lg:relative lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : 'translate-x-full'
          } ${desktopCollapsed ? 'lg:w-0 lg:overflow-hidden lg:border-0' : 'lg:w-72'}`}
          style={{ height: '100dvh', maxHeight: '100dvh' }}
        >
          <div className="flex flex-col h-full max-h-full">
            {/* Header */}
            <div className="p-3 sm:p-5 border-b border-border bg-primary text-primary-foreground">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BrandLogo size={40} />
                  <div className="leading-tight">
                    <h2 className="text-2xl sm:text-3xl font-black font-brand">قِمَم</h2>
                    <p className="text-xs sm:text-sm opacity-80 font-bold" data-testid="sidebar-branch-label">{isSuper ? (isActingAsBranch ? (branch?.name || actingBranch) : 'المنطقة الشرقية') : (branch?.name || 'الدمام الرئيسي')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-1 rounded-md hover:bg-white/10"
                  data-testid="close-sidebar-button"
                  aria-label="إغلاق القائمة"
                >
                  <X className="h-6 w-6" />
                </button>
                {/* Desktop-only collapse toggle — visible on lg+ screens */}
                <button
                  onClick={toggleDesktopSidebar}
                  className="hidden lg:inline-flex p-1 rounded-md hover:bg-white/10"
                  data-testid="collapse-sidebar-desktop"
                  aria-label="إغلاق الشريط الجانبي"
                  title="إغلاق الشريط الجانبي"
                >
                  <PanelRightClose className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-2 sm:mt-4 text-sm">
                <p className="font-bold">{user?.name}</p>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <p className="text-xs opacity-80" data-testid="sidebar-role-label">
                    {user?.role === 'super_admin' ? 'مدير عام' : user?.role === 'admin' ? 'مدير' : user?.role === 'supervisor' ? 'مشرف' : 'موظف'}
                  </p>
                  <TodayBadge />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav
              className="flex-1 p-3 space-y-1 overflow-y-auto overscroll-contain min-h-0"
              style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
              data-testid="sidebar-nav"
            >
              {navigation.map((item) => {
                const Icon = item.icon;

                if (item.children) {
                  const isOpen = !!openGroups[item.name];
                  const hasActiveChild = item.children.some((c) => c.href === location.pathname);
                  return (
                    <div key={item.name} className="space-y-1">
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.name)}
                        data-testid={`nav-group-${item.name}`}
                        className={`w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition-all ${
                          hasActiveChild
                            ? 'bg-accent text-primary'
                            : 'text-foreground hover:bg-accent hover:text-primary'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-primary" />
                          <span>{item.name}</span>
                        </span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="pr-3 space-y-1">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            const isActiveChild = location.pathname === child.href;
                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                data-testid={`nav-${child.name}`}
                                className={`flex items-center gap-3 pr-7 pl-4 py-2.5 rounded-lg text-sm font-medium transition-all border-r-2 ${
                                  isActiveChild
                                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                    : 'text-foreground/80 border-border/40 hover:bg-accent hover:text-primary'
                                }`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <ChildIcon className={`h-4 w-4 ${isActiveChild ? '' : 'text-primary'}`} />
                                <span>{child.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name}`}
                    className={`flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition-all ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'text-foreground hover:bg-accent hover:text-primary'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={`h-5 w-5 ${isActive ? '' : 'text-primary'}`} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Logout + credit */}
            <div className="p-2 sm:p-3 border-t border-border space-y-1 sm:space-y-2">
              <Button
                onClick={handleLogout}
                variant="ghost"
                data-testid="logout-button"
                className="w-full justify-start gap-3 text-destructive hover:bg-destructive/10 hover:text-destructive h-9 sm:h-10"
              >
                <LogOut className="h-5 w-5" />
                <span>تسجيل الخروج</span>
              </Button>
              <div className="credit-footer flex flex-col items-center gap-0 shrink-0" data-testid="credit-footer-sidebar">
                <p className="text-[10px] sm:text-xs text-center">رؤية وتنفيذ</p>
                <img
                  src="/credit-logo.png"
                  alt="رؤية وتنفيذ"
                  className="h-10 w-10 sm:h-20 sm:w-20 lg:h-32 lg:w-32 object-contain -mt-1"
                />
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-screen flex flex-col relative">
          {/* Desktop-only reopen button — appears when sidebar is collapsed on large screens */}
          {desktopCollapsed && (
            <button
              onClick={toggleDesktopSidebar}
              data-testid="expand-sidebar-desktop"
              aria-label="فتح الشريط الجانبي"
              title="فتح الشريط الجانبي"
              className="hidden lg:inline-flex fixed top-4 right-4 z-40 items-center gap-2 px-3 py-2 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
            >
              <PanelRightOpen className="h-5 w-5" />
              <span className="text-sm font-bold">القائمة</span>
            </button>
          )}
          {/* Mobile header — menu button on the RIGHT (RTL) */}
          <header className="lg:hidden bg-primary text-primary-foreground border-b border-border px-4 py-3 flex items-center gap-3 sticky top-0 z-30 shadow-sm">
            <button
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-button"
              aria-label="فتح القائمة"
              className="p-1 rounded-md hover:bg-white/10"
            >
              <Menu className="h-7 w-7" />
            </button>
            <div className="flex items-center gap-2 flex-1">
              <BrandLogo size={44} />
              <div className="leading-tight">
                <h1 className="text-2xl font-black font-brand">قِمَم</h1>
                <p className="text-sm opacity-80 font-bold">{isSuper ? (isActingAsBranch ? (branch?.name || actingBranch) : 'المنطقة الشرقية') : (branch?.name || 'الدمام الرئيسي')}</p>
              </div>
            </div>
          </header>

          {/* Acting-as-branch banner (super_admin only) */}
          {isActingAsBranch && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 md:px-8 py-2.5" data-testid="acting-branch-banner">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-amber-900 text-sm font-bold">
                  <Network className="h-4 w-4" />
                  أنت تتصفّح فرع <span className="text-brand_blue">{branch?.name || actingBranch}</span> كمدير عام
                </div>
                <button
                  onClick={exitActingBranch}
                  className="text-xs font-bold text-brand_blue hover:underline flex items-center gap-1"
                  data-testid="exit-acting-branch-btn"
                >
                  العودة لإدارة المنطقة الشرقية
                </button>
              </div>
            </div>
          )}

          {/* Page content */}
          <div className="flex-1 p-4 sm:p-6 md:p-8">
            {children}
          </div>

          {/* Footer */}
          <footer className="border-t border-border py-4 px-4 sm:px-6 md:px-8 bg-card">
            <div className="credit-footer flex flex-col items-center justify-center gap-0" data-testid="credit-footer">
              <p className="text-sm">رؤية وتنفيذ</p>
              <img
                src="/credit-logo.png"
                alt="رؤية وتنفيذ"
                className="h-36 w-36 object-contain -mt-1"
              />
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}

// Sunday-based ISO-style week number — matches backend
function _sunWeekNumber(d) {
  const jan1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const offset = (jan1.getUTCDay() + 7) % 7;
  const week1Start = new Date(jan1);
  week1Start.setUTCDate(jan1.getUTCDate() - offset);
  const delta = Math.floor((d - week1Start) / 86400000);
  return { year: d.getUTCFullYear(), week: Math.floor(delta / 7) + 1 };
}

const AR_WEEKDAYS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const AR_MONTHS = ['', 'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

function TodayBadge() {
  const now = new Date();
  const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const { year, week } = _sunWeekNumber(utcNow);
  const dow = AR_WEEKDAYS[now.getDay()];
  const day = now.getDate();
  const month = AR_MONTHS[now.getMonth() + 1];
  return (
    <div className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-white/10 ring-1 ring-white/20 text-[9px] leading-tight whitespace-nowrap" data-testid="header-today-badge">
      <span className="font-bold">{dow} {day} {month} {year}</span>
      <span className="opacity-70">·</span>
      <span className="opacity-90 font-numbers">أسبوع {week}</span>
    </div>
  );
}
